import { ListOption } from '@zendesk/forethought-elements';
import { SystemContextVariableType } from 'src/services/insight-cards/types';
import { ContextVariableTypes } from 'src/utils/enums';

export const systemContextVariablesOptions = [
  {
    label: '$Deflection',
    type: 'CHECKBOX',
    value: 'deflection',
  },
  {
    label: '$CSAT Score',
    type: 'NUMBER',
    value: 'csat_score',
  },
  {
    label: '$Sentiment',
    type: 'SHORT_TEXT',
    value: 'sentiment',
  },
  {
    label: '$Relevance',
    type: 'SHORT_TEXT',
    value: 'relevance',
  },
  {
    label: '$Engagement',
    type: 'CHECKBOX',
    value: 'engagement',
  },
  {
    label: '$Automated',
    type: 'CHECKBOX',
    value: 'resolution_automated',
  },
  {
    label: '$Resolved',
    type: 'SHORT_TEXT',
    value: 'resolution_tier',
  },
  // Per-conversation counts of quick feedback responses. Quick feedback is
  // per-article, so one conversation can have several (and both positive and
  // negative at once).
  {
    label: '$Quick Feedback: Positive (Count)',
    type: 'NUMBER',
    value: 'quick_feedback_positive',
  },
  {
    label: '$Quick Feedback: Negative (Count)',
    type: 'NUMBER',
    value: 'quick_feedback_negative',
  },
  // Proactive-conversations variables. Only offered to orgs with the
  // `proactive_conversations` feature flag — consumers filter with
  // PROACTIVE_CONVERSATIONS_SYSTEM_CONTEXT_VARIABLES below since this list
  // is a static array.
  {
    label: '$Conversation Type',
    type: 'SHORT_TEXT',
    value: 'conversation_type',
  },
  {
    label: '$Proactive Funnel Stage',
    type: 'SHORT_TEXT',
    value: 'proactive_funnel_stage',
  },
] as const satisfies (ListOption & {
  type: keyof typeof ContextVariableTypes;
  value: SystemContextVariableType;
})[];

// Variables gated on the `proactive_conversations` feature flag. Consumption
// sites (the context-variable dropdowns and useGetContextVariables) filter
// these out when useIsProactiveConversationsEnabled() is false.
export const PROACTIVE_CONVERSATIONS_SYSTEM_CONTEXT_VARIABLES: readonly SystemContextVariableType[] =
  ['conversation_type', 'proactive_funnel_stage'];

export const systemContextVariablesOptionsWithoutUsableSystemContextVariables =
  systemContextVariablesOptions;

// Hardcoded filter options for each SystemContextVariableType. An empty array
// means "no fixed value dropdown": the variable falls through to a free numeric
// input with comparison operators (like custom number event properties), which
// is what the unbounded quick-feedback counts use.
export const systemContextVariableFilterOptions: Record<
  SystemContextVariableType,
  ListOption[]
> = {
  conversation_type: [
    { label: 'Deflection', value: 'Deflection' },
    { label: 'Proactive Conversation', value: 'Proactive Conversation' },
  ],
  csat_score: [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
  ],
  deflection: [
    { label: 'True', value: 'True' },
    { label: 'False', value: 'False' },
  ],
  engagement: [
    { label: 'True', value: 'True' },
    { label: 'False', value: 'False' },
  ],
  proactive_funnel_stage: [
    { label: 'Engaged', value: 'Engaged' },
    { label: 'Ignored', value: 'Ignored' },
  ],
  // Empty on purpose — unbounded counts use a free numeric input + operators.
  quick_feedback_negative: [],
  quick_feedback_positive: [],
  relevance: [
    { label: 'Relevant', value: 'Relevant' },
    { label: 'Somewhat Relevant', value: 'Somewhat Relevant' },
    { label: 'Irrelevant', value: 'Irrelevant' },
  ],
  resolution_automated: [
    { label: 'True', value: 'True' },
    { label: 'False', value: 'False' },
  ],
  resolution_tier: [
    { label: 'Contained', value: 'Contained' },
    { label: 'Verified', value: 'Verified' },
    { label: 'Non automated', value: 'Non automated' },
    // The API treats `n/a` as the filter alias for null or unknown resolution tiers.
    { label: 'N/A', value: 'N/A' },
  ],
  sentiment: [
    { label: 'Positive', value: 'Positive' },
    { label: 'Neutral', value: 'Neutral' },
    { label: 'Negative', value: 'Negative' },
  ],
};
